import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconCalculator,
  IconExternalLink,
  IconPlus,
  IconSearch,
  IconTrash,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '../api';
import {
  calculateContribution,
  checkHeavyBulky,
  FBA_RATE_CARD,
  type ContributionInput,
  validateContributionInput,
} from '../features/amazon/easy-ship';
import type { SavedAmazonProduct } from '../features/amazon/saved-product';
import { apiHook } from '../treaty';

const moneyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2,
});

const numberFormatter = new Intl.NumberFormat('en-IN', {
  maximumFractionDigits: 2,
});

function contributionInput(
  product: SavedAmazonProduct,
  fulfillmentChannel: 'easyShip' | 'fba',
): ContributionInput {
  return {
    fulfillmentChannel,
    stepLevel: product.stepLevel,
    sellingPrice: product.sellingPrice,
    productCost: product.productCost,
    actualWeightGrams: product.actualWeightGrams,
    lengthCm: product.lengthCm,
    widthCm: product.widthCm,
    heightCm: product.heightCm,
    packagingCost: product.packagingCost,
    advertisingCost: 0,
    otherCost: product.otherCost,
    returnRatePercentage: product.returnRatePercentage,
    fbaShippingZone: product.fbaShippingZone,
    fbaStorageDays: product.fbaStorageDays,
    fbaInboundCost: product.fbaInboundCost,
    fbaShipInOwnContainer: product.fbaShipInOwnContainer,
  };
}

function currentMargins(product: SavedAmazonProduct) {
  const easyShipInput = contributionInput(product, 'easyShip');
  const fbaInput = contributionInput(product, 'fba');
  if (validateContributionInput(easyShipInput).length > 0) return null;
  const heavyBulky = checkHeavyBulky({
    ...fbaInput,
    actualWeightGrams:
      fbaInput.actualWeightGrams +
      (fbaInput.fbaShipInOwnContainer ? 0 : FBA_RATE_CARD.defaultPackagingWeightGrams),
  });
  if (heavyBulky.isHeavyBulky) return null;
  return {
    easyShip: calculateContribution(easyShipInput).expectedMargin,
    fba: calculateContribution(fbaInput).expectedMargin,
  };
}

export function AmazonProductsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const productsQuery = apiHook.useQuery((client) => client['amazon-products'].get());
  const [search, setSearch] = useState('');
  const normalizedSearch = search.trim().toLocaleLowerCase('en-IN');
  const products = (productsQuery.data ?? []).filter((product) =>
    product.name.toLocaleLowerCase('en-IN').includes(normalizedSearch),
  );

  function confirmDelete(product: SavedAmazonProduct) {
    modals.openConfirmModal({
      title: 'Delete saved product?',
      children: `“${product.name}” will be permanently deleted. This cannot be undone.`,
      labels: { confirm: 'Delete product', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        const response = await api.api.api.v1['amazon-products']({ id: product.id }).delete();
        if (response.error && response.status !== 404) {
          notifications.show({ color: 'red', message: 'The product could not be deleted.' });
          return;
        }
        await queryClient.invalidateQueries({ queryKey: ['amazon-products'] });
        notifications.show({ color: 'green', message: `${product.name} deleted.` });
      },
    });
  }

  return (
    <Stack gap='lg' maw={1380} mx='auto'>
      <Group justify='space-between' align='flex-end'>
        <div>
          <Title order={1}>Saved Amazon products</Title>
          <Text c='dimmed' mt={5}>
            Review shared Wall Art pricing setups and reopen them with today’s fee rules.
          </Text>
        </div>
        <Button leftSection={<IconPlus size={17} />} onClick={() => void navigate('/amazon')}>
          New calculation
        </Button>
      </Group>

      <Paper withBorder radius='lg' p='lg'>
        <Group justify='space-between'>
          <TextInput
            aria-label='Search saved products'
            placeholder='Search by product name'
            leftSection={<IconSearch size={17} />}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            w={{ base: '100%', sm: 360 }}
          />
          <Badge variant='light' size='lg'>
            {productsQuery.data?.length ?? 0} saved
          </Badge>
        </Group>
      </Paper>

      {productsQuery.isError && (
        <Alert color='red' icon={<IconAlertCircle size={18} />}>
          Saved products could not be loaded.
        </Alert>
      )}

      <Paper withBorder radius='lg' className='data-card'>
        <Table.ScrollContainer minWidth={1120}>
          <Table verticalSpacing='md' horizontalSpacing='lg' highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Product</Table.Th>
                <Table.Th>Selling price</Table.Th>
                <Table.Th>Product cost</Table.Th>
                <Table.Th>Package</Table.Th>
                <Table.Th>CPC</Table.Th>
                <Table.Th>Return rate</Table.Th>
                <Table.Th>Margins before CPC</Table.Th>
                <Table.Th>Last updated</Table.Th>
                <Table.Th ta='right'>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {productsQuery.isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={9}>
                    <Text c='dimmed' ta='center' py='xl'>
                      Loading saved products…
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {products.map((product) => {
                const margins = currentMargins(product);
                return (
                  <Table.Tr key={product.id}>
                    <Table.Td>
                      <Text fw={700}>{product.name}</Text>
                      <Text size='xs' c='dimmed'>
                        Created by {product.createdBy.email}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={600}>{moneyFormatter.format(product.sellingPrice)}</Text>
                    </Table.Td>
                    <Table.Td>{moneyFormatter.format(product.productCost)}</Table.Td>
                    <Table.Td>
                      <Text size='sm'>{numberFormatter.format(product.actualWeightGrams)} g</Text>
                      <Text size='xs' c='dimmed'>
                        {numberFormatter.format(product.lengthCm)} ×{' '}
                        {numberFormatter.format(product.widthCm)} ×{' '}
                        {numberFormatter.format(product.heightCm)} cm
                      </Text>
                    </Table.Td>
                    <Table.Td>{moneyFormatter.format(product.costPerClick)}</Table.Td>
                    <Table.Td>{numberFormatter.format(product.returnRatePercentage)}%</Table.Td>
                    <Table.Td>
                      {margins ? (
                        <Stack gap={2}>
                          <Text size='sm'>Easy Ship: {margins.easyShip.toFixed(1)}%</Text>
                          <Text size='sm'>FBA: {margins.fba.toFixed(1)}%</Text>
                        </Stack>
                      ) : (
                        <Badge color='orange' variant='light'>
                          Needs review
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size='sm'>{dayjs(product.updatedAt).format('DD MMM YYYY, HH:mm')}</Text>
                      <Text size='xs' c='dimmed'>
                        by {product.updatedBy.email}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group justify='flex-end' gap={4} wrap='nowrap'>
                        <Tooltip label='Open in calculator'>
                          <ActionIcon
                            variant='light'
                            aria-label={`Open ${product.name}`}
                            onClick={() => void navigate(`/amazon?product=${product.id}`)}
                          >
                            <IconExternalLink size={17} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label='Delete product'>
                          <ActionIcon
                            variant='subtle'
                            color='red'
                            aria-label={`Delete ${product.name}`}
                            onClick={() => confirmDelete(product)}
                          >
                            <IconTrash size={17} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {!productsQuery.isLoading && !productsQuery.isError && products.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={9}>
                    <Stack align='center' gap='xs' py='xl'>
                      <IconCalculator size={30} color='var(--mantine-color-gray-5)' />
                      <Text c='dimmed' ta='center'>
                        {normalizedSearch
                          ? 'No saved products match this search.'
                          : 'No Amazon products have been saved yet.'}
                      </Text>
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
    </Stack>
  );
}
